import { useEffect, useRef } from 'react';

/**
 * The home composer's unsent draft, kept per signed-in user.
 *
 * One browser can hold several accounts in turn (sign out and in, or a
 * sign-in link opened over another session). The draft used to live under one
 * key for the whole browser, so the next account opened the home page holding
 * the previous account's exact text (ISSUE-004, found by /qa 2026-10-02).
 */
const LEGACY_KEY = 'dashboardTaskDescription';
const PREFIX = 'dashboardTaskDescription:';

/**
 * Which draft. Landing, Full Stack, Mobile and Import share one ('build'):
 * hand-typed text deliberately survives a switch between them (Dashboard's
 * handleSurfaceChange). A video brief is a different thing to write, so the
 * Video surface keeps its own: a landing brief must never open under Video,
 * nor a video brief under Landing.
 */
export type DraftLane = 'build' | 'video';

// 'build' keeps the original key, so drafts saved before lanes existed load.
const keyFor = (userId: string, lane: DraftLane = 'build') =>
  PREFIX + userId + (lane === 'build' ? '' : `:${lane}`);

/** The user's saved draft, or ''. Drops the old browser-wide draft: it has no owner. */
export function loadDraft(userId: string, lane: DraftLane = 'build'): string {
  try {
    localStorage.removeItem(LEGACY_KEY);
    return localStorage.getItem(keyFor(userId, lane)) || '';
  } catch {
    return '';
  }
}

export function saveDraft(userId: string, text: string, lane: DraftLane = 'build'): void {
  try {
    if (text) localStorage.setItem(keyFor(userId, lane), text);
    else localStorage.removeItem(keyFor(userId, lane));
  } catch { /* private mode / quota: the draft just won't persist */ }
}

/** Every account's draft in this browser (sign-out on a shared device). */
export function clearDrafts(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k === LEGACY_KEY || k.startsWith(PREFIX))) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch { /* storage unavailable */ }
}

/**
 * Keeps `text` saved as `userId`'s draft and restores it once the user is
 * known. On an account switch in place the text is replaced, so one account's
 * draft is never filed under the next one's key. `skipRestore` lets an
 * explicit prefill (the chat bridge) win over a saved draft.
 *
 * `lane` switches which draft the text is: moving to another lane swaps the
 * leaving lane's text out (it is already saved) and that lane's own draft in.
 */
export function useAccountDraft(userId: string | undefined, text: string, setText: (t: string) => void, skipRestore: { current: boolean }, lane: DraftLane = 'build'): void {
  const owner = useRef<string | null>(null);
  const laneRef = useRef<DraftLane>(lane);
  useEffect(() => {
    if (!userId || owner.current === userId) return;
    const switching = owner.current !== null;
    owner.current = userId;
    if (skipRestore.current && !switching) return;
    const saved = loadDraft(userId, laneRef.current);
    if (saved || switching) setText(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  // Save BEFORE the lane effect below: a render that both changes the text
  // and the lane (a chip prompt cleared on a tab switch) files the text under
  // the lane it was written in, not the one being entered.
  useEffect(() => {
    if (!userId || owner.current !== userId) return;
    saveDraft(userId, text, laneRef.current);
  }, [text, userId]);
  useEffect(() => {
    if (laneRef.current === lane) return;
    laneRef.current = lane;
    if (!userId || owner.current !== userId) return;
    setText(loadDraft(userId, lane));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lane]);
}
